import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Coins, ArrowRightLeft, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Devises() {
  const [taux, setTaux] = useState([]);
  const [devises, setDevises] = useState([]);
  const [ecarts, setEcarts] = useState(null);
  const [loading, setLoading] = useState(true);
  // Converter
  const [montant, setMontant] = useState(1000);
  const [deDevise, setDeDevise] = useState('EUR');
  const [versDevise, setVersDevise] = useState('USD');
  const [conversion, setConversion] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/devises/taux`),
      axios.get(`${API_URL}/devises/devises`),
      axios.get(`${API_URL}/devises/ecarts`),
    ]).then(([t, d, e]) => { setTaux(t.data); setDevises(d.data); setEcarts(e.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const res = await axios.get(`${API_URL}/devises/convertir`, { params: { montant, de: deDevise, vers: versDevise } });
      setConversion(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setConverting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold"><Coins className="w-6 h-6 inline mr-2" />Multi-devises</h1><p className="text-slate-500">Taux BCE du jour</p></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Converter */}
        <Card>
          <CardHeader><CardTitle><ArrowRightLeft className="w-5 h-5 inline mr-2" />Convertisseur</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Montant</Label><Input type="number" value={montant} onChange={e => setMontant(parseFloat(e.target.value) || 0)} /></div>
              <div><Label>De</Label>
                <Select value={deDevise} onValueChange={setDeDevise}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{devises.map(d => <SelectItem key={d.code} value={d.code}>{d.code} — {d.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Vers</Label>
                <Select value={versDevise} onValueChange={setVersDevise}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{devises.map(d => <SelectItem key={d.code} value={d.code}>{d.code} — {d.label}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <Button onClick={handleConvert} disabled={converting} className="w-full bg-[#0F5156] hover:bg-[#0d464a]">
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />} Convertir
            </Button>
            {conversion && (
              <div className="bg-slate-50 rounded-xl p-5 text-center">
                <p className="text-sm text-slate-500">{conversion.montant_original} {conversion.devise_origine} =</p>
                <p className="text-3xl font-bold text-[#0F5156]">{conversion.montant_converti} {conversion.devise_cible}</p>
                <p className="text-xs text-slate-400 mt-1">Taux : {conversion.taux}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Exchange rate differences */}
        <Card>
          <CardHeader>
            <CardTitle>Écarts de change</CardTitle>
            <CardDescription>Sur les écritures en devise étrangère</CardDescription>
          </CardHeader>
          <CardContent>
            {ecarts && ecarts.ecarts?.length > 0 ? (
              <>
                <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                  {ecarts.type_global === 'gain' ? <TrendingUp className="w-5 h-5 text-green-500" /> : <TrendingDown className="w-5 h-5 text-red-500" />}
                  <div>
                    <p className="text-sm text-slate-500">Total écarts de change</p>
                    <p className={`text-xl font-bold ${ecarts.total_ecart_eur >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {ecarts.total_ecart_eur >= 0 ? '+' : ''}{formatCurrency(ecarts.total_ecart_eur)}
                    </p>
                  </div>
                </div>
                <Table><TableBody>
                  {ecarts.ecarts.slice(0, 5).map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{e.date}</TableCell>
                      <TableCell className="text-sm">{e.libelle}</TableCell>
                      <TableCell><Badge variant="outline">{e.devise}</Badge></TableCell>
                      <TableCell className={`text-right font-mono ${e.ecart_eur >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {e.ecart_eur >= 0 ? '+' : ''}{formatCurrency(e.ecart_eur)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              </>
            ) : (
              <p className="text-center text-slate-400 py-8">Aucun écart de change</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Exchange rates table */}
      <Card>
        <CardHeader><CardTitle>Taux de change BCE</CardTitle><CardDescription>Mis à jour quotidiennement par la Banque Centrale Européenne</CardDescription></CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-0">
            {taux.slice(0, 24).map((t, i) => (
              <div key={i} className="p-4 border-b border-r border-slate-100 text-center">
                <p className="text-sm font-bold text-slate-700">{t.devise}</p>
                <p className="text-lg font-mono text-[#0F5156]">{t.taux?.toFixed(4)}</p>
                <p className="text-[10px] text-slate-400">1 EUR =</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
